import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { appelsOffresService, analysesService } from '@/services';
import { deduplicateAnalysesByDepot } from "@/lib/analysis-dedup";
import { useToast } from "@/hooks/use-toast";
import Layout from "@/components/Layout";
import { Trophy, TrendingUp, Users, Building2, BarChart3, ChevronDown, ChevronUp, Target, Award, Download, FileDown, Eye } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip, Legend } from "recharts";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { convertScoreToFive } from "@/lib/utils";
import { ComparisonView } from "@/components/ranking/ComparisonView";
import { GlobalRankingView } from "@/components/ranking/GlobalRankingView";
import { GlobalComparisonView } from "@/components/ranking/GlobalComparisonView";
import { useRankingPdfExport } from "@/hooks/useRankingPdfExport";
import type { AnalysisRun } from "@/types";
interface AppelOffres {
  id: string;
  titre: string;
  reference: string;
}
interface SocieteRanking {
  societe_id: string;
  societe_nom: string;
  nb_candidatures: number;
  score_moyen: number;
  decision_counts: {
    shortlist: number;
    consider: number;
    reject: number;
  };
  candidats: CandidatDetail[];
  scores_criteres: {
    [key: string]: {
      score: number;
      count: number;
    };
  };
}
interface CandidatDetail {
  id: string;
  nom: string;
  score: number;
  decision: string;
  created_at: string;
  model_used?: string;
  scores_criteres?: { [key: string]: number };
}
const RankingSocietes = () => {
  const [appelOffres, setAppelOffres] = useState<AppelOffres[]>([]);
  const [selectedAO, setSelectedAO] = useState<string>("");
  const [runs, setRuns] = useState<AnalysisRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string>("latest");
  const [rankings, setRankings] = useState<SocieteRanking[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedSocietes, setExpandedSocietes] = useState<Set<string>>(new Set());
  const [topNRadar, setTopNRadar] = useState<number>(5);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(new Set());
  // State to track selected candidates per society (societe_id -> Set of candidat_ids)
  const [selectedCandidates, setSelectedCandidates] = useState<Map<string, Set<string>>>(new Map());
  const { toast } = useToast();
  const { exportRankingToPdf } = useRankingPdfExport();

  // Initialize selectedCandidates when rankings change (select all by default)
  useEffect(() => {
    if (rankings.length > 0) {
      const newSelection = new Map<string, Set<string>>();
      rankings.forEach(ranking => {
        newSelection.set(ranking.societe_id, new Set(ranking.candidats.map(c => c.id)));
      });
      setSelectedCandidates(newSelection);
    }
  }, [rankings]);

  // Helper to toggle candidate selection
  const toggleCandidateSelection = (societeId: string, candidatId: string) => {
    setSelectedCandidates(prev => {
      const newMap = new Map(prev);
      const currentSet = newMap.get(societeId) || new Set();
      const newSet = new Set(currentSet);
      if (newSet.has(candidatId)) {
        newSet.delete(candidatId);
      } else {
        newSet.add(candidatId);
      }
      newMap.set(societeId, newSet);
      return newMap;
    });
  };

  // Helper to get computed values for a ranking based on selected candidates
  const getComputedRankingValues = (ranking: SocieteRanking) => {
    const selectedSet = selectedCandidates.get(ranking.societe_id) || new Set();
    const selectedCandidatsList = ranking.candidats.filter(c => selectedSet.has(c.id));
    
    const nbSelected = selectedCandidatsList.length;
    const scoreMoyen = nbSelected > 0 
      ? selectedCandidatsList.reduce((sum, c) => sum + c.score, 0) / nbSelected 
      : 0;
    
    const decisionCounts = {
      shortlist: selectedCandidatsList.filter(c => c.decision === 'shortlist').length,
      consider: selectedCandidatsList.filter(c => c.decision === 'consider').length,
      reject: selectedCandidatsList.filter(c => c.decision === 'reject' || (c.decision !== 'shortlist' && c.decision !== 'consider')).length
    };
    
    return { nbSelected, scoreMoyen, decisionCounts };
  };

  // Compute sorted rankings based on selected candidates
  const sortedRankings = useMemo(() => {
    return [...rankings]
      .map(ranking => ({
        ...ranking,
        computed: getComputedRankingValues(ranking)
      }))
      .sort((a, b) => b.computed.scoreMoyen - a.computed.scoreMoyen);
  }, [rankings, selectedCandidates]);

  // Compute rankings with updated values for ComparisonView
  const computedRankingsForComparison = useMemo(() => {
    return sortedRankings.map(ranking => {
      const selectedSet = selectedCandidates.get(ranking.societe_id) || new Set();
      const selectedCandidatsList = ranking.candidats.filter(c => selectedSet.has(c.id));
      
      // Recalculate scores_criteres based on selected candidates
      const newScoresCriteres: { [key: string]: { score: number; count: number } } = {};
      selectedCandidatsList.forEach(candidat => {
        if (candidat.scores_criteres) {
          Object.entries(candidat.scores_criteres).forEach(([criteriaName, score]) => {
            if (!newScoresCriteres[criteriaName]) {
              newScoresCriteres[criteriaName] = { score: 0, count: 0 };
            }
            newScoresCriteres[criteriaName].score += score;
            newScoresCriteres[criteriaName].count += 1;
          });
        }
      });
      
      // Calculate averages
      Object.keys(newScoresCriteres).forEach(criteria => {
        if (newScoresCriteres[criteria].count > 0) {
          newScoresCriteres[criteria].score = newScoresCriteres[criteria].score / newScoresCriteres[criteria].count;
        }
      });
      
      return {
        ...ranking,
        nb_candidatures: ranking.computed.nbSelected,
        score_moyen: ranking.computed.scoreMoyen,
        decision_counts: ranking.computed.decisionCounts,
        candidats: selectedCandidatsList,
        scores_criteres: Object.keys(newScoresCriteres).length > 0 ? newScoresCriteres : ranking.scores_criteres
      };
    }).filter(r => r.nb_candidatures > 0);
  }, [sortedRankings, selectedCandidates]);
  
  const averageScore = computedRankingsForComparison.length > 0 
    ? computedRankingsForComparison.reduce((sum, r) => sum + r.score_moyen, 0) / computedRankingsForComparison.length 
    : 0;
  const bestScore = computedRankingsForComparison.length > 0 ? computedRankingsForComparison[0].score_moyen : 0;
  const selectedAppelOffres = appelOffres.find(ao => ao.id === selectedAO);

  // Initialize selected companies when topNRadar or rankings change
  useEffect(() => {
    if (rankings.length > 0) {
      const topN = rankings.slice(0, topNRadar);
      setSelectedCompanies(new Set(topN.map(r => r.societe_id)));
    }
  }, [topNRadar, rankings]);

  // Get available companies (top N)
  const availableCompanies = useMemo(() => {
    const topN = rankings.slice(0, topNRadar);
    const colors = ['#3b82f6', '#ef4444', '#22c55e', '#a855f7', '#f97316', '#ec4899', '#14b8a6', '#f59e0b', '#6366f1', '#6b7280'];
    return topN.map((r, i) => ({
      id: r.societe_id,
      name: r.societe_nom,
      color: colors[i % colors.length],
      score: r.score_moyen
    }));
  }, [rankings, topNRadar]);

  // Prepare radar chart data for preview (only selected companies)
  const radarPreviewData = useMemo(() => {
    if (rankings.length === 0) return { criteria: [], companies: [], radarData: [] };
    
    const allCriteria = new Set<string>();
    rankings.forEach(r => Object.keys(r.scores_criteres).forEach(c => allCriteria.add(c)));
    const criteriaList = Array.from(allCriteria);
    
    // Filter to only selected companies
    const filteredCompanies = availableCompanies.filter(c => selectedCompanies.has(c.id));
    const filteredRankings = rankings.filter(r => selectedCompanies.has(r.societe_id));

    // Transform data for Recharts radar
    const radarData = criteriaList.map(criteria => {
      const point: any = { criteria: criteria.length > 15 ? criteria.substring(0, 14) + '...' : criteria };
      filteredRankings.forEach((r, i) => {
        point[`company_${i}`] = r.scores_criteres[criteria]?.score || 0;
      });
      return point;
    });

    return { criteria: criteriaList, companies: filteredCompanies, radarData };
  }, [rankings, availableCompanies, selectedCompanies]);

  const toggleCompany = (companyId: string) => {
    setSelectedCompanies(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  };

  const selectAllCompanies = () => {
    setSelectedCompanies(new Set(availableCompanies.map(c => c.id)));
  };

  const deselectAllCompanies = () => {
    setSelectedCompanies(new Set());
  };

  const handleExportPdf = () => {
    if (selectedAppelOffres && computedRankingsForComparison.length > 0) {
      // Use computedRankingsForComparison which reflects candidate selections
      exportRankingToPdf(selectedAppelOffres, computedRankingsForComparison, topNRadar, Array.from(selectedCompanies));
      setShowPreview(false);
      const totalCandidates = computedRankingsForComparison.reduce((sum, r) => sum + r.nb_candidatures, 0);
      toast({
        title: "PDF généré",
        description: `Le rapport avec ${computedRankingsForComparison.length} sociétés et ${totalCandidates} dossiers sélectionnés a été téléchargé`
      });
    }
  };

  const fetchAppelOffres = async () => {
    try {
      const data = await appelsOffresService.getAll();
      // Sort by creation date descending
      const sortedData = (data || []).sort((a: any, b: any) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setAppelOffres(sortedData);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger les appels d'offres",
        variant: "destructive"
      });
    }
  };
  const fetchRuns = async () => {
    try {
      const runsData = await analysesService.listRuns(selectedAO);
      setRuns(runsData);
    } catch {
      // Silently ignore — runs are optional (legacy AOs may not have any)
      setRuns([]);
    }
  };
  const fetchRankings = async () => {
    if (!selectedAO) return;
    setLoading(true);
    try {
      const runFilter = (runs.length > 0 && selectedRunId !== "all") ? selectedRunId : undefined;
      const analysesData = await analysesService.list({ aoId: selectedAO, runId: runFilter });

      // Deduplicate: keep only the latest analysis per depot (candidat)
      const dedupedAnalyses = deduplicateAnalysesByDepot(
        (analysesData || []).map((a: any) => ({ ...a, depotId: a.depotId || a.depot?.id }))
      );

      // Group by société
      const societesMap = new Map<string, SocieteRanking>();
      dedupedAnalyses.forEach((analyse: any) => {
        const depot = analyse.depot;
        const candidat = depot?.candidat;
        const societe = candidat?.societe;
        if (!societe) return;
        if (!societesMap.has(societe.id)) {
          societesMap.set(societe.id, {
            societe_id: societe.id,
            societe_nom: societe.nom,
            nb_candidatures: 0,
            score_moyen: 0,
            decision_counts: {
              shortlist: 0,
              consider: 0,
              reject: 0
            },
            candidats: [],
            scores_criteres: {}
          });
        }
        const societyRanking = societesMap.get(societe.id)!;
        societyRanking.nb_candidatures++;

        // Add candidat details
        const resultatJson = analyse.resultatJson as any;
        const modelUsed = resultatJson?.analysis_metadata?.model_used;

        // Extract individual criteria scores for this candidate
        const candidatScoresCriteres: { [key: string]: number } = {};
        if (resultatJson?.criteria_analyses && Array.isArray(resultatJson.criteria_analyses)) {
          resultatJson.criteria_analyses.forEach((criteria: any) => {
            if (criteria.criterion_name && typeof criteria.criterion_score === 'number') {
              candidatScoresCriteres[criteria.criterion_name] = criteria.criterion_score;
            }
          });
        }

        societyRanking.candidats.push({
          id: candidat.id,
          nom: depot.fichierFileId || candidat.nom,
          score: analyse.overallScore || 0,
          decision: analyse.decision || 'pending',
          created_at: analyse.createdAt,
          model_used: modelUsed,
          scores_criteres: candidatScoresCriteres
        });

        // Count decisions
        if (analyse.decision === 'shortlist') societyRanking.decision_counts.shortlist++;
        else if (analyse.decision === 'consider') societyRanking.decision_counts.consider++;
        else societyRanking.decision_counts.reject++;

        // Extract scores from resultat_json
        if (resultatJson && typeof resultatJson === 'object') {
          // Process criteria_analyses - the correct path for individual criteria
          if (resultatJson.criteria_analyses && Array.isArray(resultatJson.criteria_analyses)) {
            resultatJson.criteria_analyses.forEach((criteria: any) => {
              if (criteria.criterion_name && typeof criteria.criterion_score === 'number') {
                const criteriaName = criteria.criterion_name;
                const score = criteria.criterion_score || 0;
                if (!societyRanking.scores_criteres[criteriaName]) {
                  societyRanking.scores_criteres[criteriaName] = {
                    score: 0,
                    count: 0
                  };
                }
                societyRanking.scores_criteres[criteriaName].score += score;
                societyRanking.scores_criteres[criteriaName].count += 1;
              }
            });
          }
        }
      });

      // Calculate averages and sort
      const rankingsArray = Array.from(societesMap.values()).map(societe => {
        const totalScore = societe.candidats.reduce((sum, c) => sum + c.score, 0);
        societe.score_moyen = societe.nb_candidatures > 0 ? totalScore / societe.nb_candidatures : 0;

        // Calculate average scores for criteria
        Object.keys(societe.scores_criteres).forEach(criteria => {
          const criteriaData = societe.scores_criteres[criteria];
          if (criteriaData.count > 0) {
            societe.scores_criteres[criteria].score = criteriaData.score / criteriaData.count;
          }
        });
        return societe;
      }).sort((a, b) => b.score_moyen - a.score_moyen);
      setRankings(rankingsArray);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger le classement",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppelOffres();
  }, []);
  useEffect(() => {
    if (selectedAO) {
      fetchRuns();
      setSelectedRunId("latest");
    } else {
      setRuns([]);
    }
  }, [selectedAO]);
  useEffect(() => {
    if (selectedAO) {
      fetchRankings();
    }
  }, [selectedAO, selectedRunId]);

  const toggleSocieteExpansion = (societeId: string) => {
    const newExpanded = new Set(expandedSocietes);
    if (newExpanded.has(societeId)) {
      newExpanded.delete(societeId);
    } else {
      newExpanded.add(societeId);
    }
    setExpandedSocietes(newExpanded);
  };
  const getDecisionBadge = (decision: string) => {
    switch (decision) {
      case 'shortlist':
        return <Badge variant="default" className="bg-green-500 hover:bg-green-600">Shortlist</Badge>;
      case 'consider':
        return <Badge variant="secondary" className="bg-yellow-500 hover:bg-yellow-600">Consider</Badge>;
      case 'reject':
        return <Badge variant="destructive">Reject</Badge>;
      default:
        return <Badge variant="outline">En attente</Badge>;
    }
  };
  const getDecisionColor = (decision: string) => {
    switch (decision) {
      case 'shortlist':
        return '#22c55e';
      case 'consider':
        return '#eab308';
      case 'reject':
        return '#ef4444';
      default:
        return '#94a3b8';
    }
  };
  return <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <Trophy className="h-8 w-8 text-yellow-500" />
              Ranking par Société
            </h1>
            <p className="text-muted-foreground mt-1">Classement des sociétés par  analyse</p>
          </div>
          
          <div className="flex items-center gap-4">
            <Select value={selectedAO} onValueChange={setSelectedAO}>
              <SelectTrigger className="w-80">
                <SelectValue placeholder="Sélectionner un appel d'offres" />
              </SelectTrigger>
              <SelectContent>
                {appelOffres.map(ao => <SelectItem key={ao.id} value={ao.id}>
                    {ao.reference} - {ao.titre}
                  </SelectItem>)}
              </SelectContent>
            </Select>

            {selectedAO && runs.length > 0 && (
              <Select value={selectedRunId} onValueChange={setSelectedRunId}>
                <SelectTrigger className="w-72">
                  <SelectValue placeholder="Sélectionner un run" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Dernière analyse</SelectItem>
                  {runs.map(run => (
                    <SelectItem key={run.runId} value={run.runId}>
                      {new Date(run.startedAt).toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} ({run.depotCount} dossiers{run.analysisMode ? `, ${run.analysisMode}` : ""})
                    </SelectItem>
                  ))}
                  <SelectItem value="legacy">Analyses précédentes</SelectItem>
                  <SelectItem value="all">Toutes les analyses</SelectItem>
                </SelectContent>
              </Select>
            )}

            {selectedAppelOffres && rankings.length > 0 && (
              <div className="flex items-center gap-2">
                <Select value={topNRadar.toString()} onValueChange={(v) => setTopNRadar(Number(v))}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Top N" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">Top 3</SelectItem>
                    <SelectItem value="5">Top 5</SelectItem>
                    <SelectItem value="10">Top 10</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => setShowPreview(true)} variant="outline" className="gap-2">
                  <Eye className="h-4 w-4" />
                  Analyse - PDF
                </Button>
              </div>
            )}
          </div>
        </div>

        {selectedAppelOffres && <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                {selectedAppelOffres.reference} - {selectedAppelOffres.titre}
              </CardTitle>
              <CardDescription>
                Analyse du classement des {rankings.length} sociétés participantes
              </CardDescription>
            </CardHeader>
          </Card>}

        {loading ? <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div> : rankings.length > 0 ? <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Sociétés
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{computedRankingsForComparison.length}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Total candidatures
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {computedRankingsForComparison.reduce((sum, r) => sum + r.nb_candidatures, 0)}/{rankings.reduce((sum, r) => sum + r.nb_candidatures, 0)}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Score moyen global
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  <div className="text-2xl font-bold">
                    {averageScore.toFixed(1)}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Note {convertScoreToFive(averageScore)}/5
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    Meilleur score
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  <div className="text-2xl font-bold">
                    {bestScore.toFixed(1)}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Note {convertScoreToFive(bestScore)}/5
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {sortedRankings.length > 0 ? sortedRankings[0].societe_nom : '-'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Tabs for different views */}
            <Tabs defaultValue="ranking" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="ranking">Classement</TabsTrigger>
                <TabsTrigger value="global">Classement Global</TabsTrigger>
                <TabsTrigger value="comparison">Vue Comparative</TabsTrigger>
                <TabsTrigger value="global-comparison">Vue Comparative Globale</TabsTrigger>
              </TabsList>
              
              <TabsContent value="ranking" className="space-y-6">
                {/* Ranking Table */}
                <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5" />
                  Classement des Sociétés
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Rang</TableHead>
                      <TableHead>Société</TableHead>
                      <TableHead className="text-center">Candidatures</TableHead>
                      <TableHead className="text-center">Score moyen</TableHead>
                      <TableHead className="text-center">Répartition décisions</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedRankings.map((ranking, index) => {
                      const computed = ranking.computed;
                      return <React.Fragment key={ranking.societe_id}>
                          <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggleSocieteExpansion(ranking.societe_id)}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {index === 0 && <Trophy className="h-4 w-4 text-yellow-500" />}
                                <span className="font-bold">#{index + 1}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{ranking.societe_nom}</div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline">
                                {computed.nbSelected}/{ranking.nb_candidatures}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="space-y-1">
                                <div className="font-bold">{computed.scoreMoyen.toFixed(1)}%</div>
                                <div className="text-xs text-muted-foreground">
                                  Note {convertScoreToFive(computed.scoreMoyen)}/5
                                </div>
                                <Progress value={computed.scoreMoyen} className="w-20 h-2" />
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                {computed.decisionCounts.shortlist > 0 && <Badge variant="default" className="bg-green-500 text-xs px-1">
                                    {computed.decisionCounts.shortlist}
                                  </Badge>}
                                {computed.decisionCounts.consider > 0 && <Badge variant="secondary" className="bg-yellow-500 text-xs px-1">
                                    {computed.decisionCounts.consider}
                                  </Badge>}
                                {computed.decisionCounts.reject > 0 && <Badge variant="destructive" className="text-xs px-1">
                                    {computed.decisionCounts.reject}
                                  </Badge>}
                              </div>
                            </TableCell>
                            <TableCell>
                              {expandedSocietes.has(ranking.societe_id) ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </TableCell>
                          </TableRow>
                          
                          {expandedSocietes.has(ranking.societe_id) && <TableRow>
                              <TableCell colSpan={6} className="p-0">
                                <div className="p-6 bg-muted/20 border-t">
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Candidats List */}
                                    <Card>
                                      <CardHeader>
                                        <CardTitle className="text-sm">Candidats ({computed.nbSelected}/{ranking.candidats.length} sélectionnés)</CardTitle>
                                      </CardHeader>
                                      <CardContent>
                                        <div className="space-y-3">
                                          {ranking.candidats.sort((a, b) => b.score - a.score).map(candidat => {
                                            const isSelected = selectedCandidates.get(ranking.societe_id)?.has(candidat.id) ?? false;
                                            return (
                                              <div 
                                                key={candidat.id} 
                                                className={`flex items-center justify-between p-3 bg-background rounded-lg border transition-opacity ${!isSelected ? 'opacity-50' : ''}`}
                                              >
                                                <div className="flex items-center gap-3">
                                                  <Checkbox 
                                                    checked={isSelected}
                                                    onCheckedChange={() => toggleCandidateSelection(ranking.societe_id, candidat.id)}
                                                  />
                                                  <div>
                                                    <div className="font-medium">
                                                      {candidat.nom}
                                                    </div>
                                                    <div className="text-sm text-muted-foreground space-y-0.5">
                                                      <div>Score: {candidat.score.toFixed(1)}%</div>
                                                      <div>Note: {convertScoreToFive(candidat.score)}/5</div>
                                                      {candidat.model_used && (
                                                        <div className="flex items-center gap-1">
                                                          <span>LLM:</span>
                                                          <Badge variant="outline" className="text-xs px-1.5 py-0 h-4">
                                                            {candidat.model_used.includes('gemini-3-flash') ? 'Gemini 3 Flash' :
                                                             candidat.model_used.includes('gemini-3-pro') ? 'Gemini 3 Pro' :
                                                             candidat.model_used.includes('gemini-2.5-flash') ? 'Gemini 2.5 Flash' :
                                                             candidat.model_used.includes('gemini-2.5-pro') ? 'Gemini 2.5 Pro' :
                                                             candidat.model_used}
                                                          </Badge>
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                                {getDecisionBadge(candidat.decision)}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </CardContent>
                                    </Card>

                                    {/* Decision Distribution */}
                                    <Card>
                                      <CardHeader>
                                        <CardTitle className="text-sm">Répartition des décisions</CardTitle>
                                      </CardHeader>
                                      <CardContent>
                                        <div className="h-[400px]">
                                          <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                              <Pie data={[{
                                            name: 'Shortlist',
                                            value: computed.decisionCounts.shortlist,
                                            color: '#22c55e'
                                          }, {
                                            name: 'Consider',
                                            value: computed.decisionCounts.consider,
                                            color: '#eab308'
                                          }, {
                                            name: 'Reject',
                                            value: computed.decisionCounts.reject,
                                            color: '#ef4444'
                                          }].filter(item => item.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({
                                            name,
                                            value,
                                            percent
                                          }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}>
                                                {[{
                                              name: 'Shortlist',
                                              value: computed.decisionCounts.shortlist,
                                              color: '#22c55e'
                                            }, {
                                              name: 'Consider',
                                              value: computed.decisionCounts.consider,
                                              color: '#eab308'
                                            }, {
                                              name: 'Reject',
                                              value: computed.decisionCounts.reject,
                                              color: '#ef4444'
                                            }].filter(item => item.value > 0).map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                                              </Pie>
                                              <Tooltip formatter={(value, name) => [`${value} candidats`, name]} />
                                            </PieChart>
                                          </ResponsiveContainer>
                                        </div>
                                      </CardContent>
                                    </Card>
                                  </div>

                                    {/* Criteria Charts */}
                                   {Object.keys(ranking.scores_criteres).length > 0 && <div className="mt-6">
                                       <Card>
                                         <CardHeader>
                                           <CardTitle className="text-sm">Scores moyens par critère</CardTitle>
                                         </CardHeader>
                                         <CardContent>
                                           <div className="h-[400px]">
                                             <ResponsiveContainer width="100%" height="100%">
                                               <BarChart data={Object.entries(ranking.scores_criteres).map(([criteria, data]) => ({
                                          criteria: criteria.length > 15 ? criteria.substring(0, 15) + '...' : criteria,
                                          score: data.score
                                        }))} margin={{
                                          top: 5,
                                          right: 30,
                                          left: 20,
                                          bottom: 60
                                        }}>
                                                 <CartesianGrid strokeDasharray="3 3" />
                                                 <XAxis dataKey="criteria" angle={-45} textAnchor="end" height={80} fontSize={10} />
                                                 <YAxis domain={[0, 100]} />
                                                 <Tooltip formatter={value => {
                                            const numericValue = Number(value);
                                            return [`${numericValue.toFixed(1)}% (${convertScoreToFive(numericValue)}/5)`, 'Score'];
                                          }} />
                                                 <Bar dataKey="score" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
                                               </BarChart>
                                             </ResponsiveContainer>
                                           </div>
                                         </CardContent>
                                       </Card>
                                     </div>}
                                </div>
                              </TableCell>
                            </TableRow>}
                        </React.Fragment>;
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
              </TabsContent>
              
              <TabsContent value="global" className="space-y-6">
                <GlobalRankingView aoId={selectedAO} />
              </TabsContent>
              
              <TabsContent value="comparison" className="space-y-6">
                <ComparisonView rankings={computedRankingsForComparison} />
              </TabsContent>
              
              <TabsContent value="global-comparison" className="space-y-6">
                <GlobalComparisonView 
                  aoId={selectedAO} 
                  appelOffre={selectedAppelOffres} 
                  selectedCandidatesData={computedRankingsForComparison}
                />
              </TabsContent>
            </Tabs>
          </div> : selectedAO ? <Card>
            <CardContent className="py-12 text-center">
              <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Aucune analyse trouvée</h3>
              <p className="text-muted-foreground">
                Aucune analyse n'a été trouvée pour cet appel d'offres.
              </p>
            </CardContent>
          </Card> : <Card>
            <CardContent className="py-12 text-center">
              <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Sélectionner une analyse</h3>
              <p className="text-muted-foreground">Choisissez une analyse pour voir le classement des sociétés.</p>
            </CardContent>
          </Card>}
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aperçu du Graphique Radar - {selectedCompanies.size} Sociétés Sélectionnées</DialogTitle>
            <DialogDescription>
              Sélectionnez les sociétés à inclure dans le graphique radar avant export PDF
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Radar Chart */}
            <div className="lg:col-span-2">
              {radarPreviewData.radarData && radarPreviewData.radarData.length >= 3 && selectedCompanies.size >= 2 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <RadarChart data={radarPreviewData.radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="criteria" tick={{ fontSize: 10 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9 }} />
                    {radarPreviewData.companies.map((company, index) => (
                      <Radar
                        key={company.name}
                        name={company.name}
                        dataKey={`company_${index}`}
                        stroke={company.color}
                        fill={company.color}
                        fillOpacity={0.1}
                        strokeWidth={2}
                      />
                    ))}
                    <Legend />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[400px] text-muted-foreground">
                  {selectedCompanies.size < 2 
                    ? "Sélectionnez au moins 2 sociétés pour afficher le radar"
                    : "Pas assez de critères pour afficher le radar (minimum 3)"}
                </div>
              )}
            </div>

            {/* Company Selection */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm">Sélection des sociétés</h4>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={selectAllCompanies}>
                    Tous
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={deselectAllCompanies}>
                    Aucun
                  </Button>
                </div>
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {availableCompanies.map((company, index) => {
                  const isSelected = selectedCompanies.has(company.id);
                  return (
                    <div 
                      key={company.id} 
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                        isSelected ? 'bg-primary/10 border border-primary/30' : 'bg-muted/50 hover:bg-muted'
                      }`}
                      onClick={() => toggleCompany(company.id)}
                    >
                      <Checkbox 
                        checked={isSelected}
                        onCheckedChange={() => toggleCompany(company.id)}
                        className="pointer-events-none"
                      />
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: company.color }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">#{index + 1} {company.name}</div>
                        <div className="text-xs text-muted-foreground">Score: {company.score.toFixed(1)}%</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pt-4 border-t">
                <h4 className="font-semibold text-sm mb-2">Zones de performance</h4>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-green-200" />
                    <span>70-100% Excellent</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-yellow-200" />
                    <span>40-70% Moyen</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-red-200" />
                    <span>0-40% Faible</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Select value={topNRadar.toString()} onValueChange={(v) => setTopNRadar(Number(v))}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">Top 3 sociétés</SelectItem>
                <SelectItem value="5">Top 5 sociétés</SelectItem>
                <SelectItem value="10">Top 10 sociétés</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                Annuler
              </Button>
              <Button onClick={handleExportPdf} className="gap-2">
                <FileDown className="h-4 w-4" />
                Exporter PDF
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>;
};
export default RankingSocietes;